// ============================================================
//  src/catalogo.js — Buscador del catálogo de concursos
//
//  Permite buscar concursos en Firestore (catalogoConcursos)
//  desde el modal de creación/edición.
//
//  Flujo:
//    1. Usuario escribe en el campo de búsqueda
//    2. Se consulta Firestore filtrando por tokens / nombre
//    3. Se muestra un dropdown con los resultados
//    4. Al seleccionar → se autocompledan los campos del modal
//    5. Si no encuentra → botón para solicitar nuevo concurso
// ============================================================

import { db } from './firebase.js';
import {
    collection,
    query,
    where,
    orderBy,
    limit,
    getDocs
} from 'firebase/firestore';
import { t } from './i18n.js';

// ── Estado del módulo ─────────────────────────────────────────

/** Concurso seleccionado del catálogo (null = ingresado manualmente) */
let _concursoSeleccionado = null;

/** Timer para debounce de búsqueda */
let _timerBusqueda = null;

/** Handler guardado para poder removerlo */
let _handlerInput = null;
let _handlerBlur = null;

// ── API pública ───────────────────────────────────────────────

/**
 * Inicializa el buscador en el campo del modal.
 * Llama una vez al abrir el modal.
 * @param {Function} onSolicitar - Callback al hacer clic en "Solicitar nuevo"
 */
export function inicializarBuscador(onSolicitar) {
    _concursoSeleccionado = null;

    const campo = document.getElementById('campo-concurso');
    const dropdown = document.getElementById('buscador-dropdown');
    if (!campo || !dropdown) return;

    if (_handlerInput) campo.removeEventListener('input', _handlerInput);
    if (_handlerBlur) campo.removeEventListener('blur', _handlerBlur);

    _handlerInput = () => {
        clearTimeout(_timerBusqueda);
        _timerBusqueda = setTimeout(() => _buscarConcursos(onSolicitar), 300);
    };

    _handlerBlur = () => {
        setTimeout(() => cerrarDropdown(), 200);
    };

    campo.addEventListener('input', _handlerInput);
    campo.addEventListener('blur', _handlerBlur);
}

/**
 * Devuelve el concurso seleccionado del catálogo, o null si fue manual.
 * @returns {Object|null}
 */
export function getConcursoSeleccionado() {
    return _concursoSeleccionado;
}

/**
 * Resetea el concurso seleccionado (al limpiar el formulario).
 */
export function resetearSeleccion() {
    _concursoSeleccionado = null;
    cerrarDropdown();
}

/**
 * Cierra el dropdown de resultados.
 */
export function cerrarDropdown() {
    const dropdown = document.getElementById('buscador-dropdown');
    if (dropdown) {
        dropdown.replaceChildren();
        dropdown.classList.remove('buscador-dropdown--visible');
    }
}

// ── Búsqueda en Firestore ─────────────────────────────────────

function _norm(s) {
    return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function _textoCompleto(c) {
    return [
        c.nombre,
        c.nombreN1MM,
        c.aliasPrincipal,
        ...(Array.isArray(c.aliasesBusqueda) ? c.aliasesBusqueda : []),
    ].map(_norm).join(' ');
}

function _coincide(c, tokens) {
    const txt = _textoCompleto(c);
    return tokens.every(t => txt.includes(t));
}

/**
 * Busca concursos usando todos los campos relevantes (nombre, nombreN1MM,
 * aliasPrincipal, aliasesBusqueda), ignorando tildes y mayúsculas,
 * con coincidencia parcial por token.
 */
async function _buscarConcursos(onSolicitar) {
    const campo = document.getElementById('campo-concurso');
    if (!campo) return;

    const raw = campo.value.trim();
    if (raw.length < 2) {
        cerrarDropdown();
        return;
    }

    const textoNorm = _norm(raw);
    const tokens = textoNorm.split(/\s+/).filter(Boolean);
    // Token más largo → más selectivo para la query indexada
    const tokenQuery = [...tokens].sort((a, b) => b.length - a.length)[0];

    _mostrarCargando();

    try {
        const q = query(
            collection(db, 'catalogoConcursos'),
            where('tokensBusqueda', 'array-contains', tokenQuery),
            limit(30)
        );

        const snapshot = await getDocs(q);

        let resultados = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(c => _coincide(c, tokens))
            .slice(0, 8);

        if (resultados.length === 0) {
            const q2 = query(
                collection(db, 'catalogoConcursos'),
                orderBy('nombre'),
                limit(100)
            );

            const snap2 = await getDocs(q2);

            resultados = snap2.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(c => _coincide(c, tokens))
                .slice(0, 8);
        }

        _mostrarResultados(resultados, textoNorm, onSolicitar);
    } catch (error) {
        console.error('[Catalogo] Error al buscar:', error);
        cerrarDropdown();
    }
}

// ── Renderizado del dropdown ──────────────────────────────────

function _mostrarCargando() {
    const dropdown = document.getElementById('buscador-dropdown');
    if (!dropdown) return;

    dropdown.replaceChildren();

    const contenedor = document.createElement('div');
    contenedor.className = 'buscador-dropdown__cargando';

    const spinner = document.createElement('span');
    spinner.className = 'buscador-dropdown__spinner';

    const texto = document.createTextNode(` ${t('catalogo.buscando')}`);

    contenedor.append(spinner, texto);
    dropdown.appendChild(contenedor);
    dropdown.classList.add('buscador-dropdown--visible');
}

function _mostrarResultados(resultados, textoBuscado, onSolicitar) {
    const dropdown = document.getElementById('buscador-dropdown');
    if (!dropdown) return;

    dropdown.replaceChildren();

    if (resultados.length === 0) {
        const vacio = document.createElement('div');
        vacio.className = 'buscador-dropdown__vacio';
        vacio.textContent = t('catalogo.sin.resultados', { texto: textoBuscado });

        const btnSolicitar = document.createElement('button');
        btnSolicitar.type = 'button';
        btnSolicitar.className = 'buscador-dropdown__solicitar';
        btnSolicitar.id = 'btn-solicitar-catalogo';
        btnSolicitar.textContent = `➕ ${t('catalogo.btn.solicitar')}`;
        btnSolicitar.addEventListener('mousedown', (e) => {
            e.preventDefault();
            cerrarDropdown();
            if (typeof onSolicitar === 'function') onSolicitar(textoBuscado);
        }, { once: true });

        dropdown.append(vacio, btnSolicitar);
        dropdown.classList.add('buscador-dropdown--visible');
        return;
    }

    const lista = document.createElement('ul');
    lista.className = 'buscador-dropdown__lista';

    for (const concurso of resultados) {
        const item = document.createElement('li');
        item.className = 'buscador-dropdown__item';

        const nombreSpan = document.createElement('span');
        nombreSpan.className = 'buscador-dropdown__nombre';
        _renderizarNombreResaltado(nombreSpan, concurso.nombre ?? '', textoBuscado);

        item.appendChild(nombreSpan);

        if (concurso.webPrincipal) {
            const dominio = _dominio(concurso.webPrincipal);
            if (dominio) {
                const webSpan = document.createElement('span');
                webSpan.className = 'buscador-dropdown__web';
                webSpan.textContent = dominio;
                item.appendChild(webSpan);
            }
        }

        item.addEventListener('mousedown', (e) => {
            e.preventDefault();
            _seleccionarConcurso(concurso);
        });

        lista.appendChild(item);
    }

    dropdown.appendChild(lista);

    const btnSolicitar = document.createElement('button');
    btnSolicitar.type = 'button';
    btnSolicitar.className = 'buscador-dropdown__solicitar';
    btnSolicitar.textContent = `➕ ${t('catalogo.btn.solicitar')}`;
    btnSolicitar.addEventListener('mousedown', (e) => {
        e.preventDefault();
        cerrarDropdown();
        if (typeof onSolicitar === 'function') onSolicitar(textoBuscado);
    }, { once: true });

    dropdown.appendChild(btnSolicitar);
    dropdown.classList.add('buscador-dropdown--visible');
}

// ── Selección de concurso ─────────────────────────────────────

/**
 * Al seleccionar un concurso del dropdown:
 * - Guarda la referencia
 * - Completa los campos del formulario
 * - Busca la edición del año actual para precargar fechas
 */
async function _seleccionarConcurso(concurso) {
    _concursoSeleccionado = concurso;

    const campoConcurso = document.getElementById('campo-concurso');
    if (campoConcurso) campoConcurso.value = concurso.nombre ?? '';

    const campoWeb = document.getElementById('campo-web-url');
    if (campoWeb && !campoWeb.value && concurso.webPrincipal) {
        campoWeb.value = concurso.webPrincipal;
    }

    const campoModo = document.getElementById('campo-modo');
    const modoDetectado = _obtenerModoAutocompletable(concurso);

    if (campoModo && modoDetectado) {
        campoModo.value = modoDetectado;
        campoModo.dispatchEvent(new Event('change', { bubbles: true }));
        campoModo.classList.remove('campo--error');

        const errorModo = document.getElementById('error-modo');
        if (errorModo) {
            errorModo.textContent = '';
            errorModo.classList.remove('visible');
        }
    }

    campoConcurso?.classList.remove('campo--error');

    const errorConcurso = document.getElementById('error-concurso');
    if (errorConcurso) {
        errorConcurso.textContent = '';
        errorConcurso.classList.remove('visible');
    }

    cerrarDropdown();

    const campoAño = document.getElementById('campo-año');
    const año = parseInt(campoAño?.value, 10) || new Date().getFullYear();
    await _precargarEdicion(concurso.id, año);
}

/**
 * Busca en catalogoEdiciones la edición del concurso para el año dado
 * y precarga las fechas de inicio y fin.
 */
async function _precargarEdicion(idConcurso, año) {
    try {
        const q = query(
            collection(db, 'catalogoEdiciones'),
            where('idConcurso', '==', String(idConcurso)),
            where('año', '==', año),
            limit(1)
        );

        const snapshot = await getDocs(q);
        if (snapshot.empty) return;

        const edicion = snapshot.docs[0].data();

        if (edicion.inicioUtc) {
            const campoInicio = document.getElementById('campo-fecha-inicio');
            if (campoInicio && !campoInicio.value) {
                campoInicio.value = _isoADatetimeLocal(edicion.inicioUtc);
            }
        }

        if (edicion.finUtc) {
            const campoFin = document.getElementById('campo-fecha-fin');
            if (campoFin && !campoFin.value) {
                campoFin.value = _isoADatetimeLocal(edicion.finUtc);
            }
        }
    } catch (error) {
        console.warn('[Catalogo] No se pudo precargar la edición:', error);
    }
}

// ── Helpers de autocompletado de modo ─────────────────────────

/**
 * Devuelve el modo si el texto contiene un único modo claro.
 * Si detecta más de uno, devuelve '' para no autocompletar.
 */
function _detectarModoDesdeTexto(texto) {
    const t = String(texto || '')
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    const modos = new Set();

    if (/\bCW\b/.test(t)) modos.add('CW');
    if (/\bSSB\b/.test(t) || /\bPHONE\b/.test(t)) modos.add('SSB');
    if (/\bRTTY\b/.test(t)) modos.add('RTTY');
    if (/\bFT8\b/.test(t)) modos.add('FT8');
    if (/\bFT4\b/.test(t)) modos.add('FT4');

    if (/\bPSK\b/.test(t) || /\bPSK31\b/.test(t) || /\bPSK63\b/.test(t) || /\bBPSK\b/.test(t)) {
        modos.add('PSK');
    }

    if (/\bDIGI\b/.test(t) || /\bDIGITAL\b/.test(t)) {
        modos.add('DIGI');
    }

    if (/\bFM\b/.test(t)) modos.add('FM');
    if (/\bAM\b/.test(t)) modos.add('AM');

    if (modos.size === 1) {
        return Array.from(modos)[0];
    }

    return '';
}

/**
 * Prioriza nombreN1MM porque suele venir más explícito.
 */
function _obtenerModoAutocompletable(concurso) {
    const textoBase = concurso?.nombreN1MM || concurso?.nombre || '';
    return _detectarModoDesdeTexto(textoBase);
}

// ── Helpers generales ─────────────────────────────────────────

function _renderizarNombreResaltado(contenedor, nombre, busqueda) {
    contenedor.replaceChildren();

    const nombreSeguro = String(nombre ?? '');
    const busquedaSegura = String(busqueda ?? '');

    if (!busquedaSegura) {
        contenedor.textContent = nombreSeguro;
        return;
    }

    const indice = nombreSeguro.toLowerCase().indexOf(busquedaSegura.toLowerCase());

    if (indice === -1) {
        contenedor.textContent = nombreSeguro;
        return;
    }

    const antes = nombreSeguro.substring(0, indice);
    const match = nombreSeguro.substring(indice, indice + busquedaSegura.length);
    const despues = nombreSeguro.substring(indice + busquedaSegura.length);

    if (antes) contenedor.appendChild(document.createTextNode(antes));

    const mark = document.createElement('mark');
    mark.className = 'buscador-resaltado';
    mark.textContent = match;
    contenedor.appendChild(mark);

    if (despues) contenedor.appendChild(document.createTextNode(despues));
}

/**
 * Extrae el dominio de una URL para mostrar en el dropdown.
 * @param {string} url
 * @returns {string}
 */
function _dominio(url) {
    try {
        return new URL(url).hostname.replace('www.', '');
    } catch {
        return '';
    }
}

/**
 * Convierte una fecha ISO (UTC) al formato datetime-local.
 * @param {string} iso - "2026-10-24T00:00:00.000Z"
 * @returns {string} "2026-10-24T00:00"
 */
function _isoADatetimeLocal(iso) {
    if (!iso) return '';
    return String(iso).substring(0, 16);
}